import React, { useEffect, useState, useCallback } from "react";
import api from "../api";
import { PageHeader, Card, Spinner, RequireTenant } from "../ui";
import { AjutorButton } from "../AjutorButton";
import { useAuth } from "../AuthContext";
import {
  Users, Send, MailOpen, MousePointerClick, Wallet, Flame, TrendingUp, AlertTriangle,
  Building2, Megaphone, UserCheck, UserMinus, FileText, Euro, Receipt, LifeBuoy,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const STAT_COLORS = {
  emerald: "bg-emerald-100 text-emerald-600",
  blue: "bg-blue-100 text-blue-600",
  sky: "bg-sky-100 text-sky-600",
  amber: "bg-amber-100 text-amber-600",
};
const Stat = ({ icon: Icon, label, value, sub, color = "emerald" }) => (
  <Card className="p-5">
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-500">{label}</span>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${STAT_COLORS[color]}`}>
        <Icon className="w-[18px] h-[18px]" />
      </div>
    </div>
    <div className="mt-3 text-3xl font-semibold tracking-tight text-slate-900">{value}</div>
    {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
  </Card>
);

function DashboardInner() {
  const { activeTenant } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
    } finally { setLoading(false); }
  }, [activeTenant]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner />;
  if (!data) return null;

  const e = data.emails;
  const funnel = [
    { name: "Trimise", value: e.sent, fill: "#059669" },
    { name: "Deschise", value: e.opened, fill: "#0ea5e9" },
    { name: "Click-uri", value: e.clicked, fill: "#f59e0b" },
    { name: "Esuate", value: e.failed, fill: "#ef4444" },
  ];
  const w = data.wallet;

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <Stat icon={Users} label="Contacte" value={data.contacts.total} sub={`${data.contacts.unsubscribed} dezabonate`} />
        <Stat icon={Send} label="Emailuri trimise" value={e.sent} sub={`${e.pending} in asteptare`} color="blue" />
        <Stat icon={MailOpen} label="Rata deschidere" value={`${e.open_rate}%`} sub={`${e.opened} deschideri`} color="sky" />
        <Stat icon={MousePointerClick} label="Rata click" value={`${e.click_rate}%`} sub={`${e.clicked} click-uri`} color="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-slate-400" />
            <h3 className="font-medium text-slate-900">Palnia vanzarii</h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnel} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {funnel.map((f, i) => <Cell key={i} fill={f.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <div className="space-y-4">
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Wallet className="w-4 h-4 text-slate-400" />
              <h3 className="font-medium text-slate-900">Portofel lead-uri</h3>
            </div>
            <div className="text-4xl font-semibold text-slate-900">{w.lead_quota_remaining}</div>
            <div className="text-sm text-slate-400">din {w.lead_quota_total} disponibile</div>
            <div className="mt-4 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-600" style={{ width: `${w.lead_quota_total ? (w.lead_quota_remaining / w.lead_quota_total) * 100 : 0}%` }} />
            </div>
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-slate-500">Platit</span>
              <span className="font-medium text-slate-900">{w.amount_paid_eur.toFixed(2)} EUR</span>
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <Flame className="w-4 h-4 text-orange-500" />
              <h3 className="font-medium text-slate-900">Lead-uri calde</h3>
            </div>
            <div className="flex items-end justify-between">
              <div className="text-3xl font-semibold text-slate-900">{data.leads.warm}</div>
              <div className="text-right text-sm text-slate-400">
                {data.leads.cost_per_lead_eur} EUR / lead
              </div>
            </div>
          </Card>
        </div>
      </div>

      {e.sent === 0 && (
        <div className="mt-4 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <AlertTriangle className="w-4 h-4" /> Nu ai trimis inca nicio campanie. Creeaza un sablon si o campanie pentru a incepe.
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { isSuperAdmin } = useAuth();
  return (
    <div>
      <PageHeader title="Dashboard" subtitle={isSuperAdmin ? "Privire de ansamblu asupra platformei YVOLA." : "Privire de ansamblu asupra activitatii tale de email."} />
      <AjutorButton file="ajutor_dashboard.pdf" />
      {isSuperAdmin
        ? <AdminDashboardInner />
        : <RequireTenant><DashboardInner /></RequireTenant>}
    </div>
  );
}

const nf = new Intl.NumberFormat("ro-RO");
const StatDuo = ({ icon: Icon, label, today, total, money, color = "emerald" }) => {
  const fmt = (v) => (money ? `${nf.format(v)} EUR` : nf.format(v));
  return (
    <Card className="p-5" data-testid={`admin-stat-${label}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-500">{label}</span>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${STAT_COLORS[color]}`}>
          <Icon className="w-[18px] h-[18px]" />
        </div>
      </div>
      <div className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 tabular-nums">
        {fmt(today)} <span className="text-slate-300 font-normal">/</span> {fmt(total)}
      </div>
      <div className="text-xs text-slate-400 mt-1">azi / total</div>
    </Card>
  );
};

function AdminDashboardInner() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/dashboard");
        setD(data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <Spinner />;
  if (!d) return null;

  const cards = [
    { icon: Building2, label: "Tenanti", m: d.tenanti, color: "emerald" },
    { icon: Megaphone, label: "Campanii", m: d.campanii, color: "blue" },
    { icon: Send, label: "Mail-uri totale", m: d.mailuri, color: "sky" },
    { icon: UserCheck, label: "Conturi active", m: d.conturi_active, color: "emerald" },
    { icon: AlertTriangle, label: "Bounce", m: d.bounce, color: "amber" },
    { icon: UserMinus, label: "Dezabonati", m: d.dezabonati, color: "amber" },
    { icon: FileText, label: "Proforme", m: d.proforme, color: "blue" },
    { icon: Euro, label: "Val. incasari", m: d.val_incasari, color: "emerald", money: true },
    { icon: Receipt, label: "Nr. incasari", m: d.nr_incasari, color: "sky" },
    { icon: LifeBuoy, label: "Solicitari ERP", m: d.solicitari_erp, color: "blue" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {cards.map((c) => (
          <StatDuo key={c.label} icon={c.icon} label={c.label}
            today={c.m?.today ?? 0} total={c.m?.total ?? 0}
            money={c.money} color={c.color} />
        ))}
      </div>
      {!d.delivery_source_online && (
        <div className="mt-4 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="admin-delivery-offline">
          <AlertTriangle className="w-4 h-4" /> Datele de livrare (mail-uri, bounce, dezabonari) nu au putut fi aduse de pe serverul de campanii acum.
        </div>
      )}
      <div className="mt-6 text-xs text-slate-400" data-testid="admin-build-version">
        Versiune: v2026.09.08-prod (dashboard 10 metrici)
      </div>
    </div>
  );
}
